import { Injectable, inject } from '@angular/core';
import { ToastController } from '@ionic/angular/standalone';

const DEFAULT_DISMISS_BUTTON = {
  text: 'Dismiss',
  role: 'cancel' as const,
};

/**
 * App-wide toast helper: top position, auto-dismiss, and a Dismiss action by default.
 * Prefer this over {@link ToastController} directly.
 */
export interface CrowdctrlToastOptions {
  message: string;
  duration?: number;
  position?: 'top' | 'bottom' | 'middle';
  color?: string;
  header?: string;
  icon?: string;
  cssClass?: string | string[];
  htmlAttributes?: Record<string, string>;
  layout?: 'baseline' | 'stacked';
  translucent?: boolean;
  buttons?: Array<{
    text?: string;
    icon?: string;
    role?: string;
    handler?: () => boolean | void | Promise<boolean | void>;
  }>;
  /** When false, no Dismiss button (e.g. transient status toasts). @default true */
  showDismissButton?: boolean;
  /**
   * When true, no auto-dismiss (toast stays until a button is used or manual dismiss).
   * Use for action toasts (e.g. “update available”).
   */
  sticky?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class CrowdctrlToastService {
  private readonly toastController = inject(ToastController);
  private readonly defaultDurationMs = 3000;
  private readonly defaultPosition: 'top' = 'top';

  /** Create and present a toast with app defaults. */
  async show(options: CrowdctrlToastOptions): Promise<HTMLIonToastElement> {
    const el = await this.create(options);
    await el.present();
    return el;
  }

  /** Create without presenting (e.g. dismiss manually before showing another). */
  async create(options: CrowdctrlToastOptions): Promise<HTMLIonToastElement> {
    return this.toastController.create(this.toNativeOptions(options));
  }

  private toNativeOptions(options: CrowdctrlToastOptions) {
    const {
      showDismissButton = true,
      sticky = false,
      position = this.defaultPosition,
      buttons: extraButtons,
      message,
      duration: durationOpt,
      ...rest
    } = options;

    const duration = sticky
      ? undefined
      : durationOpt !== undefined
        ? durationOpt
        : this.defaultDurationMs;

    const buttons = showDismissButton
      ? [...(extraButtons ?? []), DEFAULT_DISMISS_BUTTON]
      : extraButtons && extraButtons.length > 0
        ? extraButtons
        : undefined;

    return {
      ...rest,
      message,
      ...(duration !== undefined ? { duration } : {}),
      position,
      buttons,
    };
  }
}
